import React, { useContext, useEffect } from 'react';
import {
	checkValidation,
	getHelperText,
	ICustomer,
	ITypePersonalDataFields,
	IValidatedState,
	Locales,
	OfferContext,
	ValidatedState,
} from '@adac-mp/shared';
import { useValidatedStates } from '../../hooks/useValidatedStates';
import { Input, Typography, MemberInput, HintMessage, EmailInput, getHintContent } from '../../index';

interface IDeliveryEmailProps {
	hasMembershipBenefit?: boolean;
	labels?: ITypePersonalDataFields;
	customer?: ICustomer;
	validateFields?: boolean;
	setCustomerFilled?: (buttonstate: boolean) => void;
	updateCustomer: React.Dispatch<React.SetStateAction<ICustomer | undefined>>;
}

export const DeliveryEmail = ({
	hasMembershipBenefit,
	labels = undefined,
	customer,
	validateFields,
	setCustomerFilled = () => {},
	updateCustomer,
}: IDeliveryEmailProps) => {
	const { lang } = useContext(OfferContext);
	const fields = ['firstName', 'lastName', 'email'];

	const setDefaultCountryName = (currLang: Locales = 'de') => {
		if (currLang === 'en') return 'Germany';
		return 'Deutschland';
	};

	const fieldMap = {
		firstName: customer?.person?.firstName,
		lastName: customer?.person?.lastName,
		email: customer?.communications[0] ? customer?.communications[0].details : undefined,
	};

	const stateInit = {};
	fields.forEach((field) => {
		stateInit[field] = {
			value: fieldMap[field] ?? undefined,
			error: false,
			warning: false,
			required: !(field === 'firstName' || field === 'lastName'),
			confirmed: field !== 'email',
		};
	});

	const [state, { updateWithValidation, updateWithoutError, updateWithConfirmationAndError, hasErrors }] =
		useValidatedStates(stateInit);

	useEffect(() => {
		const states: IValidatedState[] = Object.values(state);
		const allFilled = states.every(({ value, required, confirmed }) => {
			return required ? value && !hasErrors() && confirmed : true;
		});
		setCustomerFilled(allFilled);
		updateCustomer({
			person: {
				firstName: state.firstName.value,
				lastName: state.lastName.value,
				memberNo: customer?.person?.memberNo,
			},
			addresses: [
				{
					countryCodeIso2: 'DE',
					country: setDefaultCountryName(lang),
				},
			],
			communications: [
				{
					typeId: 1,
					details: state.email.error || !state.email.confirmed ? undefined : state.email.value,
				},
			],
		} as ICustomer);
	}, [state]);

	useEffect(() => {
		if (validateFields) {
			const currStates: [string, ValidatedState][] = Object.entries(state);
			currStates.forEach(([id, currState]) =>
				updateWithConfirmationAndError(
					id,
					currState.value ?? '',
					currState.required ? !currState.value || currState.error : false,
					Boolean(currState.confirmed),
				),
			);
		}
	}, [validateFields]);

	const updateEmailValue = (value: string, validationStates?: { error?: boolean; confirmed?: boolean }) => {
		updateWithConfirmationAndError(
			'email',
			value,
			validationStates?.error ?? false,
			validationStates?.confirmed ?? false,
		);
	};

	return (
		<>
			<Typography color="title" variant="header" size="xxl" component="h4" className="px-4 mb-4 mt-5">
				{labels?.personalDataTitle || 'Persönliche Daten'}
			</Typography>
			<div className="mb-5">
				<EmailInput
					labels={{
						email: labels?.email,
						emailError: labels?.emailError,
						emailRepeat: labels?.emailRepeat,
						emailRepeatError: labels?.emailRepeatError,
					}}
					state={state.email}
					updateEmail={updateEmailValue}
				/>
			</div>
			<Input
				placeholder={labels?.name || 'Vorname'}
				mandatory={state.firstName.required}
				value={state.firstName.value}
				hasError={state.firstName.error}
				helperText={labels?.nameError || getHelperText('FIRSTNAME')}
				name="fist-name"
				autocomplete="given-name"
				onBlur={(e) => {
					updateWithValidation(
						'firstName',
						e.target.value,
						() => state.firstName.required && checkValidation(e.target.value, 'STRING'),
					);
				}}
				onChange={(e) => {
					updateWithoutError('firstName', e.target.value);
				}}
			/>
			<Input
				placeholder={labels?.lastName || 'Nachname'}
				mandatory={state.lastName.required}
				value={state.lastName.value}
				hasError={state.lastName.error}
				helperText={labels?.lastNameError || getHelperText('LASTNAME')}
				name="last-name"
				autocomplete="family-name"
				onBlur={(e) => {
					updateWithValidation(
						'lastName',
						e.target.value,
						() => state.lastName.required && checkValidation(e.target.value, 'STRING'),
					);
				}}
				onChange={(e) => {
					updateWithoutError('lastName', e.target.value);
				}}
			/>
			{/* Business Logic: Rendering MemberInput without benefits and as optional Input */}
			{!hasMembershipBenefit && (
				<MemberInput customer={customer} setCustomer={updateCustomer} setMemberSuccess={setCustomerFilled} />
			)}
			<HintMessage
				className="mt-4"
				hints={getHintContent(
					'personalDataHint',
					[
						{
							tip: 'Hinweis:',
							message:
								'Die Erhebung und Verarbeitung persönlicher Daten erfolgt ausschließlich zu Autorisierungszwecken in der Zahlungsabwicklung.',
						},
					],
					labels?.hints,
				)}
			/>
		</>
	);
};
